import { useMemo } from 'react'
import { useGetLiveListConfigs } from '@/services/hooks/relay/dynamic'
import { RollupKeyDependencyMap } from '@/utils/rollup-key-dependency'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { RollupKeyField } from './useRollupKeyFields'

/**
 * The rollup key's dependency graph: which eligible key info type requires
 * which other one. Derived from the same `GET /v2/relay/live_data/config`
 * response the app's live-list pickers already fetch — no dedicated
 * endpoint for this. An edge exists wherever a `LiveListConfig`'s
 * `filter_config` has a `doc_row`-scope, `required: true` FilterSource: the
 * config's own `info_types` is the dependent, `source.info_types` are
 * candidate ancestors.
 *
 * Scoped down to the project's eligible key fields (`keyFields`): a
 * FilterSource's `info_types` can list namespaced variants of the same
 * canonical info type that don't apply to this project, so anything not
 * also a `keyFields` member is dropped rather than surfaced as a dangling
 * dependency the UI could never satisfy.
 */
export default function useRollupKeyDependencies(keyFields: RollupKeyField[]) {
  const { project } = useProjectContext()

  const { liveListConfigs, isPending } = useGetLiveListConfigs({
    orgCredentialTypeId: project.org_credential_type_id || '',
    enabled: !!project.org_credential_type_id && keyFields.length > 0,
  })

  const requiredAncestorsByInfoTypeId = useMemo<RollupKeyDependencyMap>(() => {
    const eligibleIds = new Set(keyFields.map((field) => field.infoTypeId))
    const edges: RollupKeyDependencyMap = new Map()
    if (!liveListConfigs) return edges

    liveListConfigs.live_list_configs.forEach((config) => {
      const dependentIds = config.info_types.filter((id) => eligibleIds.has(id))
      if (dependentIds.length === 0) return

      config.filter_config.fields.forEach((field) => {
        const source = field.source
        if (!source || source.scope !== 'doc_row' || !source.required) return

        const ancestorIds = source.info_types.filter((id) =>
          eligibleIds.has(id),
        )
        if (ancestorIds.length === 0) return

        dependentIds.forEach((dependentId) => {
          let ancestors = edges.get(dependentId)
          if (!ancestors) {
            ancestors = new Set()
            edges.set(dependentId, ancestors)
          }
          ancestorIds.forEach((ancestorId) => ancestors.add(ancestorId))
        })
      })
    })

    return edges
  }, [keyFields, liveListConfigs])

  return {
    requiredAncestorsByInfoTypeId,
    // Only meaningfully pending while there's something to depend on —
    // mirrors useRollupKeyFields's own isPending gating.
    isPending: keyFields.length > 0 && isPending,
  }
}
